import { Component, ViewEncapsulation } from '@angular/core';
import { Location } from '@angular/common';
import { QueryParamService, Core } from '../shared/core/ovs.core';
import { ResortDetailClientBase } from '../services/resort-detail.client';
import { ResortDetail, ResortInventory, ResortImage, ResortImageType } from '../models/presentation.model';
import { ResortInventoryGroup } from '../models/browser.model';
import { PageScrollConfig } from 'ngx-page-scroll';
import { Geometry, Coordinates, CoordinateType } from '../shared/geo/ovs.geocoding';
import { ResortComponent } from '../shared/resort.component';
import { MatDialog, MatDialogRef } from '@angular/material';
import { RoomAvailabilityComponent } from './room-availability/room-availability.component';
import { UpdateClientService } from '../services/update-client.service';
import { StorageService } from '../services/storage-service.client';
import { ResortDetailsPageEvents, ResortPageViews } from '../instrumentation/resort-custom-events';
import { Router } from '@angular/router';
import { UserActivityLogger, UserActivityPublishDataTypes, UserActivityLoggerOptions } from '../instrumentation/user-activity-logger.service';

@Component({
    selector: 'resort-detail',
    templateUrl: './resort-detail.component.html',
    styleUrls: ['./resort-detail.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class ResortDetailComponent extends ResortComponent {
    private _resortClient: ResortDetailClientBase;
    private _dialog: MatDialog;
    private _roomDialog: MatDialogRef<RoomAvailabilityComponent, ResortInventory>;

    public resort: ResortDetail;
    public groupRI: ResortInventoryGroup;
    public inventoryGroups: ResortInventoryGroup[];
    public cheapestInventory: ResortInventory;
    public isExclusive: boolean;
    public content: string;
    public useBrowserBackOverride: boolean;
    public resortImages: ResortImage[];
    public showDestinationExtendedInfo: string = null;
    public showSpaExtendedInfo: string = null;
    public showExploreExtendedInfo: string = null;
    public showDineExtendedInfo: string = null;
    public showToDoExtendedInfo: string = null;
    public showAmenitiesExtendedInfo: boolean;
    public mapGeometry: Geometry;
    private _core: Core;

    public get hasDiscount(): boolean {
        return this.cheapestInventory && this.cheapestInventory.hasDiscount;
    }

    public get memberRate(): number {
        return this.cheapestInventory.getMemberRate(this.configuration.ShowPricePerDay);
    }

    constructor(private router: Router,
        resortClient: ResortDetailClientBase,
        dialog: MatDialog,
        private updateClientService: UpdateClientService,
        private storageService: StorageService,
        private qryParamService: QueryParamService,
        private location: Location, core: Core) {
        super();
        this._dialog = dialog;
        this._resortClient = resortClient;
        PageScrollConfig.defaultScrollOffset = 125;
        PageScrollConfig.defaultDuration = 500;
        this._core = core;
    }

    handleErr(e) {
        this.mapAppInsightsInfo(e);
    }

    protected initialize(): void {
        try {
            super.initialize();

            window.scroll(0, 0);


            const resortId = this.qryParamService.resortDetailParams.resortId;
            const startDate = this.qryParamService.resortDetailParams.startDate; //<any>new Date(Date.parse(this.qryParamService.getParam("startDate"))); 
            this.isExclusive = this.qryParamService.resortDetailParams.isExclusive;
            var self = this;
            this.updateClientService.EnsureLatestVersion(() => {
                try {
                    this._resortClient.Get(resortId, startDate, v => this.onGetResortDetailCompleted(v),self.handleErr.bind(this));
                } catch (ex) {
                    this.mapAppInsightsInfo(ex);
                }
            });

            this.useBrowserBackOverride = this.configuration.UseBrowserBackOnResortDetailsPage;
        } catch (ex) {
            this.mapAppInsightsInfo(ex);
        }

    }

    private onGetResortDetailCompleted(resortDetail: ResortDetail): void {
        try {
            this.qryParamService.resortListingParams.isDirect = false;

            this.resort = resortDetail;

            let geo: Geometry = new Geometry();
            geo.Coordinates = new Coordinates(this.resort.latitude, this.resort.longitude, CoordinateType.Center);
            this.mapGeometry = geo;

            this.resort.isExclusive = this.isExclusive;

            const groups: ResortInventoryGroup[] = [];

            const keys: string[] = this.resort.resortInventoryList.select(i => i.key).distinct().toArray();

            keys.forEach(key => {
                const inventories = this.resort.resortInventoryList.where(i => i.key === key).toArray();
                groups.add(new ResortInventoryGroup(this.resort, inventories));
            });

            this.inventoryGroups = groups;
            const minDiscountedPrice = this.resort.resortInventoryList.min(i => i.hasDiscount ? i.dailyDiscountedPrice : i.dailyPrice);
            this.cheapestInventory = this.getCheapestInventory(minDiscountedPrice);
            this.groupRI = new ResortInventoryGroup(resortDetail, resortDetail.resortInventoryList);
            UserActivityLogger.publish(this.application.configuration, UserActivityPublishDataTypes.ApplicationConfiguration);
            UserActivityLogger.publish(this.configuration.Member, UserActivityPublishDataTypes.Member);
            UserActivityLogger.publish(this.resort, UserActivityPublishDataTypes.ResortDetail);
            UserActivityLogger.logPageView(ResortPageViews.ResortDetailsPreferred, UserActivityLoggerOptions.Both, {
                memberRate: this.cheapestInventory.discountedPrice
            });

            this.getResortCreditDescription();

            this.setExtendedInfo();
        } catch (ex) {
            this.mapAppInsightsInfo(ex);
        }

    }

    public getCheapestInventory(minPrice: number): ResortInventory {
        const selectedInventory = this.resort.resortInventoryList;
        let minDiscountedPrice = 0;
        if (this.configuration.ShowPricePerDay) {
            minDiscountedPrice = selectedInventory.min(i => i.hasDiscount ? i.dailyDiscountedPrice : i.dailyPrice);
            this.cheapestInventory = selectedInventory.first(i => i.hasDiscount ? i.dailyDiscountedPrice === minDiscountedPrice : i.dailyPrice === minDiscountedPrice);
        }
        else {
            minDiscountedPrice = selectedInventory.min(i => i.hasDiscount ? i.discountedPrice : i.price);
            this.cheapestInventory = selectedInventory.first(i => i.hasDiscount ? i.discountedPrice === minDiscountedPrice : i.price === minDiscountedPrice);
        }

        return this.cheapestInventory;
    }

    private setExtendedInfo() {
        //standard extended info module images
        const resortImageTypes = this.resort.resortImageTypeList;
        this.showDestinationExtendedInfo = this.extendedImageExists(resortImageTypes, [ResortImageType.Destination]);
        this.showSpaExtendedInfo = this.extendedImageExists(resortImageTypes, [ResortImageType.Spa]);
        this.showExploreExtendedInfo = this.extendedImageExists(resortImageTypes, [ResortImageType.Property]);
        this.showDineExtendedInfo = this.extendedImageExists(resortImageTypes, [ResortImageType.Dining]);
        this.showToDoExtendedInfo = this.extendedImageExists(resortImageTypes, [ResortImageType.Activities]);

        //amenities module image check
        this.showAmenitiesExtendedInfo = this.otherExtendedImageExists(this.resort.resortImageList, [ResortImageType.Amenities]);
    }

    private bookCheapest(): void {
        this.application.checkOut(this.resort, this.cheapestInventory);
    }

    public IsMenuOpen: boolean = false;
    private menuBreakPoint: number = 1007; // Screen width + scrollbar width
    public ToggleMenu(): void {
        if (window.innerWidth < this.menuBreakPoint) {
            this.IsMenuOpen = !this.IsMenuOpen;
        }
    }

    public book(inventory: any): void {
        this.updateClientService.EnsureLatestVersion(() => {
            this.application.checkOut(this.resort, <ResortInventory>inventory, false);
        });
    }

    public bookWithUpgrade(inventory: any): void {
        this.application.checkOut(this.resort, <ResortInventory>inventory, true);
    }

    public showCompleteAvailability(): void {
        UserActivityLogger.logEvent(ResortDetailsPageEvents.HeroAvailabilityButtonClicked, {}, UserActivityLoggerOptions.AppInsightsOnly);
        try {
            this._roomDialog = this._dialog.open(RoomAvailabilityComponent, { data: this.groupRI });
            this._roomDialog.afterClosed().subscribe(inventory => {
                if (inventory)
                    this.book(inventory);
            });
        } catch (ex) {
            this.mapAppInsightsInfo(ex);
        }

    }

    public navClick(title: string) {
        UserActivityLogger.logEvent(ResortDetailsPageEvents.NavigationClicked, { navTitle: title }, UserActivityLoggerOptions.AppInsightsOnly);
    }

    public goBrowserHistoryBack() {
        const logoNavigationUrl = this.configuration.Logos ? this.configuration.Logos.first().NavigationUrl : "";
        if (logoNavigationUrl === "") {
            this.location.back();
        } else {
            window.location.href = logoNavigationUrl;
        }
    }
    public goBack() {
        this.router.navigate(["search-resorts"], { queryParams: { sid: this.qryParamService.encodedGuid } });
    }

    public hideResortCreditMessage(): boolean {
        return this._core.currentConfiguration.HideResortCreditMessage;
    }

    private getResortCreditDescription() {
        if (this.resort !== undefined &&
            this.resort !== null &&
            this.resort.resortCreditDetails !== undefined &&
            this.resort.resortCreditDetails !== null) {
            this.content = this.resort.resortCreditDetails;
        }
    }

    private mapAppInsightsInfo(ex) {
        const mapData = new Map();
        mapData.set("memberid", this.application.configuration.Member.Id);
        mapData.set("ovcpartner", this.application.configuration.Partner.LongName);
        mapData.set("memberclass", this.application.configuration.MemberClass);
        mapData.set("product", "Resort");
        mapData.set("errormessage", ex.message ? ex.message : "Details exception unknown");
        mapData.set("httperrorcode", ex.code ? ex.code : ex.status ? ex.status : '');
        mapData.set("origination", "ICE");
        mapData.set("resortdetailfailed", JSON.stringify(ex));
        let jsonObject = {};
        mapData.forEach((value, key) => {
            jsonObject[key] = value;
        });

        UserActivityLogger.logEvent(ResortDetailsPageEvents.ResortDetailFailure, jsonObject, UserActivityLoggerOptions.AppInsightsOnly);
    }
}



// WEBPACK FOOTER //
// /home/vsts/work/1/s/Ovs.Resort.UI.Web/src/app/resort-detail/resort-detail.component.ts